import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api, setSiteToken, getSiteToken, setVaultToken, formatApiError, themeAudioUrl,
} from "@/lib/api";

export default function Passcode() {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [theme, setTheme] = useState(null);
  const [audioOn, setAudioOn] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const audioRef = useRef(null);
  const lastTapRef = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (getSiteToken()) {
      nav("/portfolio", { replace: true });
      return;
    }
    api.getTheme().then(setTheme).catch(() => setTheme(null));
  }, [nav]);

  const startAudio = async () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.volume = 0.55;
      await audioRef.current.play();
      setAudioOn(true);
    } catch { setAudioOn(false); }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioOn) { audioRef.current.pause(); setAudioOn(false); }
    else { startAudio(); }
  };

  // Double-tap / double-click on the "F" opens the vault passcode modal
  const handleFTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 450) {
      lastTapRef.current = 0;
      setVaultOpen(true);
    } else {
      lastTapRef.current = now;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!value) { setError("Enter the passcode"); return; }
    if (theme?.enabled && theme?.has_audio && !audioOn) startAudio();
    setSubmitting(true);
    try {
      const res = await api.verifyPasscode(value);
      setSiteToken(res.token);
      setExiting(true);
      if (audioRef.current) {
        const el = audioRef.current;
        const startVol = el.volume;
        const t0 = Date.now();
        const fade = setInterval(() => {
          const t = (Date.now() - t0) / 800;
          if (t >= 1) { el.volume = 0; clearInterval(fade); return; }
          el.volume = startVol * (1 - t);
        }, 40);
      }
      setTimeout(() => nav("/portfolio", { replace: true }), 850);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  const themeReady = theme?.enabled && theme?.has_audio;

  return (
    <div className="grain min-h-screen w-full flex flex-col items-center justify-center bg-[var(--fsyl-bg)] relative overflow-hidden" data-testid="passcode-page">
      <div className="absolute inset-0 scanline pointer-events-none opacity-30" />
      <div className={`max-w-xl w-full px-8 ${exiting ? "glitch-out" : "fade-up"}`}>
        <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-dim)] mb-6 uppercase">
          // SECURE TERMINAL — ACCESS REQUIRED
        </div>
        <h1 className="font-display font-black text-7xl md:text-[9rem] tracking-tighter leading-none mb-2 select-none" data-testid="passcode-title">
          <span
            role="button"
            tabIndex={0}
            onClick={handleFTap}
            onKeyDown={(e) => { if (e.key === "Enter") handleFTap(); }}
            data-testid="vault-trigger"
            aria-label="."
            className="cursor-pointer inline-block hover:text-[var(--fsyl-cyan)] transition-colors"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            F
          </span>
          YSL
        </h1>
        <div className="font-mono-fsyl text-[11px] tracking-widest text-[var(--fsyl-muted)] mb-12">
          A PRIVATE PHOTOGRAPHY ARCHIVE
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block font-mono-fsyl text-[11px] tracking-widest text-[var(--fsyl-cyan)] terminal-cursor">
            ENTER PASSCODE
          </label>
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            data-testid="passcode-input"
            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[var(--fsyl-cyan)] outline-none font-mono-fsyl text-2xl py-3 px-1 text-white placeholder-white/20 caret-[var(--fsyl-cyan)]"
            placeholder="•••••••••••"
            disabled={submitting}
          />
          {error && (
            <div role="alert" className="font-mono-fsyl text-[11px] tracking-widest text-red-400 pt-2" data-testid="passcode-error">
              ✕ {error.toUpperCase()}
            </div>
          )}
          <div className="pt-6 flex items-center justify-between">
            <button
              type="submit"
              disabled={submitting}
              data-testid="passcode-submit"
              className="font-mono-fsyl text-[12px] tracking-[0.3em] uppercase px-6 py-3 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] transition-colors disabled:opacity-40"
            >
              {submitting ? "VERIFYING..." : "→ ENTER"}
            </button>
            <button
              type="button"
              onClick={() => nav("/admin/login")}
              data-testid="passcode-to-admin"
              className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-dim)] hover:text-[var(--fsyl-muted)] transition"
            >
              ADMIN ↗
            </button>
          </div>
        </form>
      </div>

      {themeReady && (
        <>
          <audio ref={audioRef} src={themeAudioUrl()} loop preload="auto" />
          <button
            type="button"
            onClick={toggleAudio}
            data-testid="passcode-audio-toggle"
            aria-label={audioOn ? "Mute theme" : "Play theme"}
            className="fixed bottom-6 right-6 z-40 w-11 h-11 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] bg-black/40 backdrop-blur-sm font-mono-fsyl text-[10px] tracking-widest transition"
          >
            {audioOn ? "◉" : "▶"}
          </button>
        </>
      )}

      <div className="fixed bottom-6 left-8 right-24 flex justify-between font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)] pointer-events-none">
        <span>FYSL.ARCHIVE / v1.0</span>
        <span>© {new Date().getFullYear()}</span>
      </div>

      {vaultOpen && (
        <VaultUnlock
          onClose={() => setVaultOpen(false)}
          onSuccess={() => { setVaultOpen(false); nav("/vault"); }}
        />
      )}
    </div>
  );
}

const VaultUnlock = ({ onClose, onSuccess }) => {
  const [pc, setPc] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.verifyVaultPasscode(pc);
      setVaultToken(res.token);
      onSuccess();
    } catch (e2) {
      setErr(formatApiError(e2));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-6" data-testid="vault-unlock-modal" onClick={onClose}>
      <div className="bg-[var(--fsyl-surface)] border border-white/10 max-w-md w-full p-8 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">// VAULT</div>
        <h3 className="font-display font-medium text-3xl tracking-tighter mb-2">For close eyes only.</h3>
        <p className="font-reflection italic text-base text-[var(--fsyl-muted)] mb-8">Enter the vault passcode.</p>

        <form onSubmit={submit} className="space-y-5">
          <input
            type="password"
            value={pc}
            onChange={(e) => setPc(e.target.value)}
            autoFocus
            data-testid="vault-unlock-input"
            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[var(--fsyl-cyan)] outline-none font-mono-fsyl text-xl py-2 px-1 text-white placeholder-white/20 caret-[var(--fsyl-cyan)]"
            placeholder="••••••"
            disabled={busy}
          />
          {err && (
            <div role="alert" data-testid="vault-unlock-error" className="font-mono-fsyl text-[11px] tracking-widest text-red-400">
              ✕ {err.toUpperCase()}
            </div>
          )}
          <div className="flex items-center justify-between pt-4">
            <button type="button" onClick={onClose} data-testid="vault-unlock-cancel" className="font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-dim)] hover:text-[var(--fsyl-muted)]">
              CANCEL
            </button>
            <button type="submit" disabled={busy} data-testid="vault-unlock-submit" className="font-mono-fsyl text-[11px] tracking-[0.3em] uppercase px-5 py-2.5 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] transition disabled:opacity-40">
              {busy ? "UNLOCKING..." : "→ UNLOCK"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
